import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FiEdit2, FiTrash2, FiPlus, FiToggleLeft, FiToggleRight } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCodesAdmin = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '0',
    max_uses: '',
    expires_at: '',
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        code: formData.code,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_purchase: parseFloat(formData.min_purchase) || 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
      };

      await axios.post(`${API}/promo-codes`, submitData);
      toast.success('Promo code created successfully');
      fetchPromoCodes();
      closeModal();
    } catch (error) {
      console.error('Error creating promo code:', error);
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/promo-codes/${code}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast.error('Failed to delete promo code');
    }
  };

  const handleToggle = async (code) => {
    try {
      await axios.patch(`${API}/promo-codes/${code}/toggle`);
      toast.success('Promo code status updated');
      fetchPromoCodes();
    } catch (error) {
      console.error('Error toggling promo code:', error);
      toast.error('Failed to update promo code');
    }
  };

  const openCreateModal = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase: '0',
      max_uses: '',
      expires_at: '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  if (loading) {
    return <div className="text-center py-12">Loading promo codes...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-semibold text-primary-main">Manage Promo Codes</h2>
        <button
          onClick={openCreateModal}
          className="btn-primary flex items-center gap-2"
          data-testid="create-promo-button"
        >
          <FiPlus /> Create Promo Code
        </button>
      </div>

      {/* Promo Codes Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Code</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Type</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Value</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Min Purchase</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Uses</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Status</th>
                <th className="px-6 py-4 text-right text-sm font-heading font-semibold text-primary-main">Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-text-muted">
                    No promo codes yet. Create your first one!
                  </td>
                </tr>
              ) : (
                promoCodes.map((promo) => (
                  <tr key={promo.id} className="border-t border-slate-100">
                    <td className="px-6 py-4 text-sm font-mono font-semibold text-primary-main">{promo.code}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary capitalize">{promo.discount_type}</td>
                    <td className="px-6 py-4 text-sm font-mono text-text-primary">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-text-secondary">${promo.min_purchase}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {promo.current_uses} / {promo.max_uses || '∞'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        promo.active ? 'bg-success/10 text-success' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {promo.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleToggle(promo.code)}
                        className="text-accent-main hover:text-accent-main/80"
                        title={promo.active ? 'Deactivate' : 'Activate'}
                      >
                        {promo.active ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => handleDelete(promo.code)}
                        className="text-error hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <h3 className="text-2xl font-heading font-bold text-primary-main mb-6">Create Promo Code</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Code *</label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      placeholder="SAVE20"
                      className="w-full border border-slate-200 rounded-md px-4 py-2 uppercase focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Discount Type *</label>
                    <select
                      name="discount_type"
                      value={formData.discount_type}
                      onChange={handleChange}
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleChange}
                      required
                      placeholder={formData.discount_type === 'percentage' ? '20' : '10.00'}
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Min Purchase ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="min_purchase"
                      value={formData.min_purchase}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Max Uses (optional)</label>
                    <input
                      type="number"
                      name="max_uses"
                      value={formData.max_uses}
                      onChange={handleChange}
                      placeholder="Unlimited"
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Expires At (optional)</label>
                    <input
                      type="datetime-local"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleChange}
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Create Promo Code
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodesAdmin;
